/**
 * THE PUBLIC HELP ROUTES (artemis-product-help design D15 § "Rewritten over
 * the corpus", spec `product-knowledge-corpus` scenario "The public help
 * center serves the corpus without sign-in"; bead 9.3).
 *
 * THE PARAM IS THE CORPUS ADDRESS. `/help/articles/:slug` carries an
 * article's filename slug and `/help/categories/:area` carries an area
 * directory name — the same two strings the corpus itself is keyed by, the
 * server-side `Help::Corpus` names, and `?articleId=` / `?categoryId=` carry
 * in the workspace venue. v1 carried `:articleId` (matched by scanning for
 * `lowerCase(article.name)`) and `:categoryId` (an ARRAY POSITION in a
 * hand-maintained tree), so re-titling an article or inserting a category
 * broke saved links. The ROUTE NAMES are deliberately unchanged: a dozen
 * `router.push({ name: "HelpArticle" })` sites keep resolving, and only the
 * params key moves.
 *
 * ── WHY THE RECORDS LIVE HERE AND NOT IN `router/index.js` ──────────────────
 * Two other surfaces need to know WHICH routes are the public help venue, not
 * merely how to push one:
 *
 *   - `stores/auth.js` — signing out on a help page leaves the member where
 *     they were reading; signing out anywhere else lands them on the map. The
 *     help center is the corpus and needs no account, so all three pages are
 *     equally a valid place to be signed out on.
 *   - `stores/documentation.js` — `helpViews` tells the Help surfaces they are
 *     rendering on a public help page. (Where a help LINK goes is not this
 *     roster's call: that switch keys on `meta.workspace`, so these pages,
 *     like every page outside the workspace, open the public article.)
 *
 * One frozen roster DERIVED FROM THE RECORDS is what keeps those two answers
 * equal to what the router actually mounts. A hand-listed roster beside the
 * records can drift from them; the substring test this replaced
 * (`_.includes(route.name, "Help")`) could not drift but could not be precise
 * either — a future `HelpfulTips` route would have read as the public help
 * venue. `router/retiredAdminRoutes.js` is the same shape for the same
 * reason: route records that more than the router has an opinion about.
 *
 * The help pages are authored light-only, so each record declares
 * `meta.surfaceTheme: "light"` and App.vue pins the resolved app theme to it
 * while the page is mounted (th-989tg).
 */

import { RETIRED_HELP_SLUGS } from "@/router/retiredHelpSlugs";

/**
 * A renamed article's old URL redirects to its current slug, keeping the
 * query and the section hash, and replacing the history entry so Back does not
 * land on the old address again (`router/retiredHelpSlugs.js`).
 *
 * @param {import("vue-router").RouteLocationNormalized} to
 * @returns {true|import("vue-router").RouteLocationRaw}
 */
export function redirectRetiredHelpSlug(to) {
  if (!Object.hasOwn(RETIRED_HELP_SLUGS, to.params.slug)) return true;

  return {
    name: "HelpArticle",
    params: { slug: RETIRED_HELP_SLUGS[to.params.slug] },
    query: to.query,
    hash: to.hash,
    replace: true,
  };
}

export const helpRouteRecords = Object.freeze([
  {
    path: "/help/categories/:area",
    name: "HelpCategories",
    props: true,
    component: () => import("@/views/HelpCategories.vue"),
    meta: { surfaceTheme: "light" },
  },
  {
    path: "/help/articles/:slug",
    name: "HelpArticle",
    props: true,
    component: () => import("@/views/HelpArticle.vue"),
    beforeEnter: redirectRetiredHelpSlug,
    meta: { surfaceTheme: "light" },
  },
  {
    path: "/help",
    name: "HelpHome",
    component: () => import("@/views/HelpHome.vue"),
    meta: { surfaceTheme: "light" },
  },
]);

/** The three public help route names, in the order the router mounts them. */
export const HELP_ROUTE_NAMES = Object.freeze(
  helpRouteRecords.map((record) => record.name),
);

/**
 * Is this the public help venue — the `/help` pages that need no account?
 *
 * @param {string|symbol|null|undefined} routeName - `route.name`.
 * @returns {boolean}
 */
export function isHelpRoute(routeName) {
  return HELP_ROUTE_NAMES.includes(routeName);
}
